import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ArrowLeft, Flag, Calendar, Users, MoreVertical, Trash2, Share2 } from "lucide-react";

export default function RoundsHistory() {
  const navigate = useNavigate();
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRounds = useCallback(async () => {
    try {
      const [v1Res, v2Res] = await Promise.all([
        fetch(`${API}/rounds`, { credentials: "include" }),
        fetch(`${API}/v2/rounds`, { credentials: "include" }),
      ]);
      const v1 = v1Res.ok ? await v1Res.json() : [];
      const v2 = v2Res.ok ? (await v2Res.json()).map(r => ({ ...r, is_v2: true })) : [];
      setRounds([...v2, ...v1]);
    } catch {
      toast.error("Error al cargar las rondas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRounds(); }, [fetchRounds]);

  const handleRoundClick = (round) => {
    if (round.is_v2) {
      if (round.status === "completed") navigate(`/play/${round.round_id}/results`);
      else navigate(`/play/${round.round_id}`);
      return;
    }
    if (round.status === "setup") navigate(`/round/${round.round_id}/players`);
    else if (round.status === "in_progress") navigate(`/round/${round.round_id}/play`);
    else navigate(`/round/${round.round_id}`);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const url = deleteTarget.is_v2
        ? `${API}/v2/rounds/${deleteTarget.round_id}`
        : `${API}/rounds/${deleteTarget.round_id}`;
      const res = await fetch(url, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error();
      setRounds(prev => prev.filter(r => r.round_id !== deleteTarget.round_id));
      toast.success("Ronda eliminada");
    } catch {
      toast.error("Error al eliminar la ronda");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const statusBadge = (status) => {
    const map = {
      setup: ["Configurando", "bg-yellow-100 text-yellow-700"],
      in_progress: ["En juego", "bg-blue-100 text-blue-700"],
      completed: ["Completada", "bg-green-100 text-green-700"],
    };
    const [txt, cls] = map[status] || ["", "hidden"];
    return <span className={`text-xs px-2 py-1 rounded-full ${cls}`}>{txt}</span>;
  };

  return (
    <div className="min-h-screen bg-[#F7F3EA]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-[#0F172A]">Historial de Rondas</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#103B31] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rounds.length === 0 ? (
          <div className="text-center py-16">
            <img src="/cadii/mascots/scorecard_walk.png" alt="" className="w-20 h-20 object-contain mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No tienes rondas aún</p>
            <Button onClick={() => navigate("/create-round")} className="bg-[#103B31] hover:bg-[#092B25]" data-testid="empty-new-round-btn">
              Empezar Ronda
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {rounds.map((round) => (
              <div key={round.round_id}
                className="bg-white rounded-2xl border border-gray-100 p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleRoundClick(round)}
                data-testid={`round-card-${round.round_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h4 className="font-bold text-[#0F172A]">{round.name}</h4>
                      {round.is_shared && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          <Share2 className="w-3 h-3" /> Compartida
                        </span>
                      )}
                      {statusBadge(round.status)}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {round.date}</span>
                      <span className="flex items-center gap-1">
                        <Flag className="w-3.5 h-3.5" />
                        {typeof round.course === "object" ? round.course?.course_name : round.course}
                      </span>
                      {round.players?.length > 0 && (
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {round.players.length}</span>
                      )}
                    </div>
                  </div>
                  {!round.is_shared && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`round-menu-${round.round_id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(round); }}
                          className="text-red-600" data-testid={`round-delete-${round.round_id}`}>
                          <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta ronda?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán todos los scores y resultados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              {deleting ? "Eliminando..." : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
